import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { FileTextIcon } from "lucide-react";
import type {
    BeFeedbackItem,
    RubricCriterion,
    RubricScores,
    CoachingDimension,
} from "@type/training";
import {
    RUBRIC_LABELS,
    COACHING_DIMENSION_LABELS,
    classifyRubricCriterion,
} from "@type/training";
import { SpeechCoachingStrip } from "@component/training/speech-coaching-strip";
import { CoachingMomentCard } from "@component/training/coaching-moment-card";
import { PerformanceInsightsBanner } from "@component/training/performance-insights-banner";
import { ResultsOverviewTab } from "@component/training/results-overview-tab";
import { KnowledgeDocDrawer } from "@component/training/knowledge-doc-drawer";
import { getKbDocument, type KbDocument } from "@service/knowledge-base";
import { buildSpeechMetrics } from "@util/training-feedback-adapter";
import type { TrainingSession } from "@type/training";
import { cn } from "@util/tailwind";

// Panel tabs = an Overview tab (parity with the AIQM detail page) plus the three
// coaching dimensions.
type PanelTab = "overview" | CoachingDimension;

interface SessionCoachingPanelProps {
    session: TrainingSession;
    rubricCriteria?: RubricCriterion[];
    onPlayFromTimestamp?: (timestamp: string) => void;
    isEmployer?: boolean;
}

export interface SessionCoachingPanelHandle {
    // Switch to the tab whose dimension owns the feedback item at this
    // timestamp (and clear any category filter so the item is visible). Used
    // when a timeline dot in the RecordingPlayer is clicked, so the right
    // panel jumps to the matching Issue Resolution / Communication / Delivery
    // tab instead of leaving the user on whatever tab they were on.
    focusTimestamp: (timestamp: string) => void;
}

/** Third-person singular → base form, for verbs that actually appear in the
 *  scorer's coaching copy. An explicit map rather than a "strip trailing -s"
 *  rule: that would produce "misse"/"addresse" on -es verbs and would corrupt
 *  plural nouns ("You results in confusion"). Unlisted verbs are left alone —
 *  an occasional "You needs" reads better than mangled prose. */
const THIRD_PERSON_VERB_FIXES: Record<string, string> = {
    is: "are", was: "were", has: "have", does: "do", "doesn't": "don't",
    "hasn't": "haven't", "isn't": "aren't", "wasn't": "weren't",
    needs: "need", demonstrates: "demonstrate", shows: "show",
    provides: "provide", struggles: "struggle", uses: "use",
    maintains: "maintain", handles: "handle", lacks: "lack", fails: "fail",
    misses: "miss", offers: "offer", gives: "give", takes: "take",
    makes: "make", asks: "ask", responds: "respond",
    acknowledges: "acknowledge", explains: "explain", confirms: "confirm",
    states: "state", says: "say", attempts: "attempt", appears: "appear",
    seems: "seem", listens: "listen", repeats: "repeat",
    interrupts: "interrupt", escalates: "escalate", resolves: "resolve",
    clarifies: "clarify", understands: "understand", addresses: "address",
    manages: "manage", keeps: "keep", tends: "tend", begins: "begin",
    ends: "end", continues: "continue", starts: "start", closes: "close",
    apologises: "apologise", apologizes: "apologize", recognises: "recognise",
    recognizes: "recognize", identifies: "identify", suggests: "suggest",
};

const THIRD_PERSON_VERB_RE = new RegExp(
    `\\b(You|you) (${Object.keys(THIRD_PERSON_VERB_FIXES).join("|")})\\b`,
    "g",
);

/** Rewrites the scorer's third-person copy for the trainee's own results page.
 *
 *  This is a presentation-layer patch, not a translation: the BE authors every
 *  result in the third person for the employer dashboard, and regex can't cover
 *  arbitrary LLM prose. The durable fix is second-person generation BE-side.
 *  Until then this handles the constructions that actually occur. */
function personalizeText(text: string, isEmployer: boolean): string {
    if (isEmployer) return text;
    return (
        text
            // Possessives FIRST — "The trainee" also matches inside "The
            // trainee's" (there's a word boundary before the apostrophe), so
            // running the bare-noun rule first would leave "You's".
            .replace(/\bThe trainee's\b/g, "Your")
            .replace(/\bthe trainee's\b/g, "your")
            .replace(/\btrainee's\b/g, "your")
            .replace(/\bThe trainee\b/g, "You")
            .replace(/\bthe trainee\b/g, "you")
            .replace(/\bTrainee\b/g, "You")
            .replace(/\btrainee\b/g, "you")
            .replace(/\bTheir\b/g, "Your")
            .replace(/\btheir\b/g, "your")
            .replace(/\bThey\b/g, "You")
            .replace(/\bthey\b/g, "you")
            // Verb agreement, applied after every subject swap so it sees the
            // full set of "You <third-person verb>" pairs.
            .replace(THIRD_PERSON_VERB_RE, (_m, subj: string, verb: string) =>
                `${subj} ${THIRD_PERSON_VERB_FIXES[verb]}`)
    );
}

/** Trainee-facing copy for one feedback item. The BE writes every result in the
 *  third person for the employer dashboard, so the trainee view rewrites the
 *  AI-authored strings — title and coach's note. Rubric text ("what great looks
 *  like") is deliberately left alone: it's employer-authored standard wording,
 *  not a statement about this person. */
function personalizeItem(item: BeFeedbackItem, isEmployer: boolean): BeFeedbackItem {
    if (isEmployer) return item;
    return {
        ...item,
        title: personalizeText(item.title, false),
        content: personalizeText(item.content, false),
    };
}

const CHIP_INACTIVE: Record<BeFeedbackItem["category"], string> = {
    Brilliant: "bg-purple-50 text-purple-700 border-purple-100 hover:bg-purple-100",
    Excellent: "bg-green-50 text-green-700 border-green-100 hover:bg-green-100",
    Good: "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100",
    Inaccuracy: "bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100",
    Mistake: "bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-100",
};

const CHIP_ACTIVE: Record<BeFeedbackItem["category"], string> = {
    Brilliant: "bg-purple-600 text-white border-purple-600 shadow-sm",
    Excellent: "bg-green-600 text-white border-green-600 shadow-sm",
    Good: "bg-emerald-600 text-white border-emerald-600 shadow-sm",
    Inaccuracy: "bg-amber-500 text-white border-amber-500 shadow-sm",
    Mistake: "bg-rose-600 text-white border-rose-600 shadow-sm",
};

const CHIP_COUNT_INACTIVE: Record<BeFeedbackItem["category"], string> = {
    Brilliant: "bg-purple-100 text-purple-800",
    Excellent: "bg-green-100 text-green-800",
    Good: "bg-emerald-100 text-emerald-800",
    Inaccuracy: "bg-amber-100 text-amber-800",
    Mistake: "bg-rose-100 text-rose-800",
};

const DIMENSION_ORDER: CoachingDimension[] = ["handling", "communication", "delivery"];

const ALL_CATEGORIES: BeFeedbackItem["category"][] = [
    "Brilliant",
    "Excellent",
    "Good",
    "Inaccuracy",
    "Mistake",
];

function scoreChipColors(value: number): string {
    if (value >= 4) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (value >= 3) return "bg-blue-50 text-blue-700 border-blue-200";
    if (value >= 2) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-rose-50 text-rose-700 border-rose-200";
}

interface DimensionData {
    rubricKeys: (keyof RubricScores)[];
    feedbackItems: BeFeedbackItem[];
}

function buildDimensionMap(
    session: TrainingSession,
    rubricCriteria?: RubricCriterion[],
): Record<CoachingDimension, DimensionData> {
    const map: Record<CoachingDimension, DimensionData> = {
        handling: { rubricKeys: [], feedbackItems: [] },
        communication: { rubricKeys: [], feedbackItems: [] },
        delivery: { rubricKeys: [], feedbackItems: [] },
    };

    if (session.rubric_scores) {
        for (const key of Object.keys(session.rubric_scores) as (keyof RubricScores)[]) {
            const label = RUBRIC_LABELS[key] ?? key;
            const dim = classifyRubricCriterion(label);
            map[dim].rubricKeys.push(key);
        }
    }

    const items = session.feedback_items ?? [];
    const criteriaEntries: {
        name: string;
        criterion: RubricCriterion;
        dim: CoachingDimension;
    }[] = [];

    if (rubricCriteria) {
        for (const rc of rubricCriteria) {
            const dim = classifyRubricCriterion(rc.name);
            criteriaEntries.push({ name: rc.name.toLowerCase(), criterion: rc, dim });
        }
    }

    for (const item of items) {
        let matched = false;
        const titleLower = item.title.toLowerCase();
        const contentLower = item.content.toLowerCase();

        for (const entry of criteriaEntries) {
            if (titleLower.includes(entry.name) || contentLower.includes(entry.name)) {
                map[entry.dim].feedbackItems.push(item);
                matched = true;
                break;
            }
        }

        if (!matched) {
            const isPositive =
                item.category === "Brilliant" ||
                item.category === "Excellent" ||
                item.category === "Good";
            if (isPositive) {
                map.communication.feedbackItems.push(item);
            } else {
                map.handling.feedbackItems.push(item);
            }
        }
    }

    return map;
}

function findMatchingCriterion(
    item: BeFeedbackItem,
    rubricCriteria?: RubricCriterion[],
): RubricCriterion | undefined {
    if (!rubricCriteria) return undefined;
    const titleLower = item.title.toLowerCase();
    const contentLower = item.content.toLowerCase();
    return rubricCriteria.find((rc) => {
        const name = rc.name.toLowerCase();
        return titleLower.includes(name) || contentLower.includes(name);
    });
}

function sortByTimestamp(items: BeFeedbackItem[]): BeFeedbackItem[] {
    return [...items].sort((a, b) => {
        const ta = a.timestamp ?? "";
        const tb = b.timestamp ?? "";
        return ta.localeCompare(tb);
    });
}

export const SessionCoachingPanel = forwardRef<
    SessionCoachingPanelHandle,
    SessionCoachingPanelProps
>(function SessionCoachingPanel({
    session,
    rubricCriteria,
    onPlayFromTimestamp,
    isEmployer = false,
}, ref) {
    // Overview is always the landing tab, including while scoring is still in
    // progress: the tab is never disabled and renders its own skeleton until the
    // rubric lands (see the overview branch below), so it reads as "loading"
    // rather than being blocked.
    const hasOverviewData = session.rubric_scores != null;
    const [activeTab, setActiveTab] = useState<PanelTab>("overview");
    const [categoryFilter, setCategoryFilter] = useState<
        BeFeedbackItem["category"] | "all"
    >("all");
    // The feedback item to auto-expand, plus a monotonic counter so re-clicking
    // the same timeline dot re-opens its card even after a manual collapse.
    const [focus, setFocus] = useState<{ ts: string; n: number } | null>(null);
    // Citation chips open the same drawer the Overview Sources rows use, so a
    // document reads identically wherever it's cited.
    const [sourceDoc, setSourceDoc] = useState<KbDocument | null>(null);
    const sourceDocSeq = useRef(0);

    // Guarded against out-of-order responses: this drawer asserts WHICH document
    // backed a coaching claim, so showing a stale one is an attribution bug, not
    // a cosmetic glitch. Clicking B while A is in flight must never resolve to A,
    // and a failed B must not leave A open looking like it succeeded.
    async function openSourceDoc(documentId: string) {
        const seq = ++sourceDocSeq.current;
        setSourceDoc(null);
        try {
            const doc = await getKbDocument(documentId);
            if (sourceDocSeq.current !== seq) return;
            setSourceDoc(doc);
        } catch {
            if (sourceDocSeq.current !== seq) return;
            setSourceDoc(null);
        }
    }

    // Navigating to another session invalidates any in-flight fetch, so a late
    // response can't open a drawer over the new session's results.
    useEffect(() => {
        sourceDocSeq.current++;
        setSourceDoc(null);
    }, [session.id]);

    // The panel stays mounted across session changes — the results page polls
    // and calls setSession when scoring completes (unscored → scored), and
    // switching attempts re-navigates the same route with a new token. Without
    // re-syncing, the tab/filter seeded at mount goes stale: an "overview"
    // selection from a scored attempt leaks onto a new unscored one (empty
    // placeholders), or Overview never activates once scoring lands. Reset on
    // session identity + score availability to keep the panel aligned.
    useEffect(() => {
        setActiveTab("overview");
        setCategoryFilter("all");
    }, [session.id]);

    const dimensionMap = buildDimensionMap(session, rubricCriteria);
    const metrics = buildSpeechMetrics(session);

    useImperativeHandle(
        ref,
        () => ({
            focusTimestamp(timestamp: string) {
                const ts = (timestamp ?? "").trim();
                if (!ts) return;
                for (const dim of DIMENSION_ORDER) {
                    const owns = dimensionMap[dim].feedbackItems.some(
                        (i) => (i.timestamp ?? "").trim() === ts,
                    );
                    if (owns) {
                        setActiveTab(dim);
                        setCategoryFilter("all");
                        setFocus((prev) => ({ ts, n: (prev?.n ?? 0) + 1 }));
                        return;
                    }
                }
            },
        }),
        [dimensionMap],
    );

    const criterionByKey = new Map<string, RubricCriterion>();
    if (rubricCriteria) {
        for (const rc of rubricCriteria) {
            const normalised = rc.name.toLowerCase().replace(/\s+/g, "_");
            for (const key of Object.keys(RUBRIC_LABELS)) {
                if (normalised.includes(key) || key.includes(normalised)) {
                    criterionByKey.set(key, rc);
                }
            }
        }
    }

    // The Overview tab has no dimension data; fall back to "handling" so the
    // dimension-specific derivations stay valid (they're only rendered when a
    // dimension tab is active).
    const dimForData: CoachingDimension = activeTab === "overview" ? "handling" : activeTab;
    const tabData = dimensionMap[dimForData];
    const filteredItems =
        categoryFilter === "all"
            ? sortByTimestamp(tabData.feedbackItems)
            : sortByTimestamp(
                  tabData.feedbackItems.filter((i) => i.category === categoryFilter),
              );

    const availableCategories = ALL_CATEGORIES.filter((c) =>
        tabData.feedbackItems.some((i) => i.category === c),
    );

    const totalFeedbackCount = tabData.feedbackItems.length;
    const categoryCounts = Object.fromEntries(
        ALL_CATEGORIES.map((c) => [
            c,
            tabData.feedbackItems.filter((i) => i.category === c).length,
        ]),
    ) as Record<BeFeedbackItem["category"], number>;

    return (
        // On lg+ the panel is a fixed-height flex column: the insights banner and
        // the tab bar stay put while only the active tab's content scrolls (see
        // the scroll wrapper below). Below lg it's a normal block.
        <div className="lg:h-full lg:flex lg:flex-col lg:min-h-0">
            {/* Insights card banner */}
            {session.qualitative_feedback && (
                <PerformanceInsightsBanner
                    title={isEmployer ? "Trainee Performance Insights" : "Roleplay Performance Insights"}
                    className="mb-5 lg:flex-shrink-0"
                >
                    {personalizeText(session.qualitative_feedback, isEmployer)}
                </PerformanceInsightsBanner>
            )}

            {/* Tab bar */}
            <div className="flex border-b border-gray-200 mb-[10px] lg:flex-shrink-0">
                <button
                    type="button"
                    onClick={() => {
                        setActiveTab("overview");
                        setCategoryFilter("all");
                    }}
                    className={cn(
                        "py-3 px-5 text-sm font-medium rounded-none border-b-2 -mb-px transition-colors whitespace-nowrap",
                        activeTab === "overview"
                            ? "border-blue-600 text-blue-600"
                            : "border-transparent text-gray-600 hover:text-gray-800",
                    )}
                >
                    Overview
                </button>
                {DIMENSION_ORDER.map((dim) => (
                    <button
                        key={dim}
                        type="button"
                        onClick={() => {
                            setActiveTab(dim);
                            setCategoryFilter("all");
                        }}
                        className={cn(
                            "py-3 px-5 text-sm font-medium rounded-none border-b-2 -mb-px transition-colors whitespace-nowrap",
                            activeTab === dim
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-600 hover:text-gray-800",
                        )}
                    >
                        {COACHING_DIMENSION_LABELS[dim].title}
                    </button>
                ))}
            </div>

            {/* Scroll region: only the active tab's content scrolls on lg+, so
                the banner and tab bar above stay fixed. */}
            <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
            {activeTab === "overview" ? (
                // Still scoring and no rubric yet → shimmer skeleton, so the tab
                // reads as loading rather than showing empty placeholders. Once
                // scoring finishes (or fails, which surfaces its own empty
                // states), render the real Overview.
                !hasOverviewData &&
                session.status !== "completed" &&
                session.status !== "failed" ? (
                    <CoachingPanelSkeleton variant="overview" />
                ) : (
                    <ResultsOverviewTab
                        conversationFlow={session.conversation_flow_assessment}
                        rubricScores={session.rubric_scores}
                        rubricTotal={session.total_score}
                        rubricCriteria={rubricCriteria}
                        actionItems={session.action_items}
                        knowledgeManifest={session.knowledge_manifest}
                        showSources={isEmployer}
                    />
                )
            ) : (
            <>
            {/* Scoring still in progress — show shimmering skeleton instead
                of the "not ready" empty state so the page reads as actively
                loading. The skeleton mimics rubric chips + filter + cards
                for the dimension tabs. */}
            {activeTab !== "delivery" &&
            session.status !== "completed" &&
            session.status !== "failed" &&
            tabData.feedbackItems.length === 0 ? (
                <CoachingPanelSkeleton variant="dimension" />
            ) : activeTab === "delivery" &&
              session.status !== "completed" &&
              session.status !== "failed" &&
              !session.speech_metrics ? (
                <CoachingPanelSkeleton variant="delivery" />
            ) : activeTab !== "delivery" ? (
                <>
                    {/* Rubric score chips */}
                    {tabData.rubricKeys.length > 0 && session.rubric_scores && (
                        <div className="flex flex-wrap gap-2 px-5 pt-3 pb-2">
                            {tabData.rubricKeys.map((key) => {
                                const value = session.rubric_scores![key];
                                const label = RUBRIC_LABELS[key] ?? key;
                                return (
                                    <span
                                        key={key}
                                        className={cn(
                                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium",
                                            scoreChipColors(value),
                                        )}
                                    >
                                        <span>{label}</span>
                                        <span className="font-bold tabular-nums">
                                            {value}/5
                                        </span>
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {/* Category filter */}
                    {availableCategories.length > 0 && (
                        <div className="px-5 py-3 border-b border-gray-100">
                            <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium mb-2">
                                Filter by category
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setCategoryFilter("all")}
                                    className="focus:outline-none rounded-full"
                                >
                                    <span
                                        className={cn(
                                            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                                            categoryFilter === "all"
                                                ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                                : "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100",
                                        )}
                                    >
                                        All
                                        {totalFeedbackCount > 0 && (
                                            <span
                                                className={cn(
                                                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums",
                                                    categoryFilter === "all"
                                                        ? "bg-blue-800/40 text-white"
                                                        : "bg-blue-100 text-blue-800",
                                                )}
                                            >
                                                {totalFeedbackCount}
                                            </span>
                                        )}
                                    </span>
                                </button>
                                {availableCategories.map((cat) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => setCategoryFilter(cat)}
                                        className="focus:outline-none rounded-full"
                                    >
                                        <span
                                            className={cn(
                                                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                                                categoryFilter === cat
                                                    ? CHIP_ACTIVE[cat]
                                                    : CHIP_INACTIVE[cat],
                                            )}
                                        >
                                            {cat}
                                            <span
                                                className={cn(
                                                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums",
                                                    categoryFilter === cat
                                                        ? "bg-black/20 text-white"
                                                        : CHIP_COUNT_INACTIVE[cat],
                                                )}
                                            >
                                                {categoryCounts[cat]}
                                            </span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Feedback items. Below lg the list caps + scrolls on its own
                        (stacked page). On lg+ the panel's outer scroll region owns
                        the scrolling, so this must not add a second scrollbar. */}
                    {filteredItems.length > 0 ? (
                        <div className="px-5 py-4 max-h-[600px] overflow-y-auto lg:max-h-none lg:overflow-visible">
                            <div className="divide-y divide-gray-100">
                                {/* Provenance (citation chips) is employer-only: it exposes
                                    which internal documents the evaluator was given, which
                                    isn't the trainee's to audit. Mirrors the Overview tab,
                                    which gates its Sources section on isEmployer too. */}
                                {filteredItems.map((item, idx) => (
                                    <CoachingMomentCard
                                        key={`${activeTab}-${idx}`}
                                        item={personalizeItem(item, isEmployer)}
                                        rubricCriterion={findMatchingCriterion(
                                            item,
                                            rubricCriteria,
                                        )}
                                        knowledgeManifest={isEmployer ? session.knowledge_manifest : null}
                                        onOpenSourceDoc={isEmployer ? openSourceDoc : undefined}
                                        onPlayFromTimestamp={onPlayFromTimestamp}
                                        quoteLabel={isEmployer ? "Quoted from their answer" : "Quoted from your answer"}
                                        // Open the top moment by default so each dimension tab
                                        // (Issue Resolution / Communication) lands on something
                                        // readable instead of all-collapsed rows.
                                        defaultExpanded={idx === 0}
                                        expandKey={
                                            focus && (item.timestamp ?? "").trim() === focus.ts
                                                ? focus.n
                                                : null
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    ) : tabData.feedbackItems.length === 0 ? (
                        <CoachingEmptyState dimension={dimForData} />
                    ) : (
                        // Items exist for this dimension but none match the
                        // active category filter — give a lighter hint instead
                        // of the full empty state.
                        <p className="text-sm text-gray-400 italic px-5 py-8 text-center">
                            No items match this filter.
                        </p>
                    )}
                </>
            ) : session.status === "failed" && !session.speech_metrics ? (
                <CoachingEmptyState
                    dimension="delivery"
                    title="Speech analysis unavailable."
                    description="We couldn't analyse the delivery for this session. Try recording another roleplay."
                />
            ) : (
                <div className="px-5 py-4 space-y-5">
                    <SpeechCoachingStrip metrics={metrics} />

                    {/* Filler words breakdown — always shown when speech_metrics is available */}
                    {session.speech_metrics && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Filler Words Breakdown
                            </p>
                            {Object.keys(session.speech_metrics.filler_breakdown ?? {}).length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(session.speech_metrics.filler_breakdown)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([word, count]) => (
                                            <span
                                                key={word}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 font-medium"
                                            >
                                                {word.charAt(0).toUpperCase() + word.slice(1)}
                                                <span className="text-gray-400">x{count}</span>
                                            </span>
                                        ))}
                                </div>
                            ) : (
                                <CoachingEmptyState
                                    dimension="delivery"
                                    title="No filler words detected."
                                    description="Nice cadence — no 'um', 'uh', or 'like' picked up in this session."
                                />
                            )}
                        </div>
                    )}

                    {/* Delivery coaching moments */}
                    {tabData.feedbackItems.length > 0 && (
                        <div>
                            <div className="divide-y divide-gray-100">
                                {tabData.feedbackItems.map((item, idx) => (
                                    <CoachingMomentCard
                                        key={`delivery-${idx}`}
                                        item={personalizeItem(item, isEmployer)}
                                        rubricCriterion={findMatchingCriterion(
                                            item,
                                            rubricCriteria,
                                        )}
                                        knowledgeManifest={isEmployer ? session.knowledge_manifest : null}
                                        onOpenSourceDoc={isEmployer ? openSourceDoc : undefined}
                                        onPlayFromTimestamp={onPlayFromTimestamp}
                                        quoteLabel={isEmployer ? "Quoted from their answer" : "Quoted from your answer"}
                                        expandKey={
                                            focus && (item.timestamp ?? "").trim() === focus.ts
                                                ? focus.n
                                                : null
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            </>
            )}
            </div>
            {/* Opened by a citation chip. Lands on the extracted tab — someone
                following a citation wants the facts the scorer used, not the
                raw PDF. */}
            <KnowledgeDocDrawer
                doc={sourceDoc}
                onClose={() => setSourceDoc(null)}
                initialTab="extracted"
            />
        </div>
    );
});

// Shared empty state matching the Transcript empty state on the same page —
// gray circular icon container, soft title, descriptive subtitle. Used for
// each coaching tab (Issue Resolution / Communication / Delivery) when the
// scoring pipeline hasn't produced data yet, so the page reads consistently
// across the left and right panels.
function CoachingEmptyState({
    dimension,
    title,
    description,
}: {
    dimension: CoachingDimension;
    title?: string;
    description?: string;
}) {
    const dimLabel = COACHING_DIMENSION_LABELS[dimension].title;
    const resolvedTitle = title ?? `${dimLabel} insights not ready yet.`;
    const resolvedDescription =
        description ??
        `Your ${dimLabel.toLowerCase()} coaching insights will appear here once the scoring pipeline finishes processing.`;
    return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-4">
                <FileTextIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">
                {resolvedTitle}
            </h3>
            <p className="text-xs text-gray-500 max-w-xs">
                {resolvedDescription}
            </p>
        </div>
    );
}

// Shimmering skeleton for the dimension / delivery tabs while scoring is
// still in flight. Mimics the actual content shapes (rubric chips + filter
// + coaching cards, or speech tiles + filler chips) so the page reads as
// actively loading rather than empty.
function CoachingPanelSkeleton({ variant }: { variant: "dimension" | "delivery" | "overview" }) {
    if (variant === "overview") {
        // Mirrors ResultsOverviewTab: a Conversation Flow paragraph, a rubric
        // score block, and an Action Items list.
        return (
            <div className="space-y-6 px-5 pt-3 pb-4" aria-busy="true" aria-live="polite">
                <div>
                    <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mb-3" />
                    <div className="space-y-2">
                        <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-11/12 bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
                    </div>
                    <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="h-6 w-28 bg-gray-100 rounded-full animate-pulse"
                            />
                        ))}
                    </div>
                </div>
                <div>
                    <div className="h-3 w-24 bg-gray-100 rounded animate-pulse mb-3" />
                    <div className="space-y-2.5">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="rounded-lg border border-gray-100 p-3 space-y-2 animate-pulse"
                            >
                                <div className="h-3 w-2/5 bg-gray-200 rounded" />
                                <div className="h-3 w-4/5 bg-gray-100 rounded" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    if (variant === "delivery") {
        return (
            <div className="px-5 py-4 space-y-4" aria-busy="true" aria-live="polite">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-2 animate-pulse"
                        >
                            <div className="h-3 w-16 bg-gray-200 rounded" />
                            <div className="h-6 w-12 bg-gray-200 rounded" />
                            <div className="h-2 w-20 bg-gray-100 rounded" />
                        </div>
                    ))}
                </div>
                <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
                <div className="pt-2">
                    <div className="h-3 w-40 bg-gray-100 rounded animate-pulse mb-3" />
                    <div className="flex flex-wrap gap-2">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-6 w-20 bg-gray-100 rounded-full animate-pulse"
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-4" aria-busy="true" aria-live="polite">
            {/* Rubric chips row */}
            <div className="flex flex-wrap gap-2 px-5 pt-3">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="h-6 w-28 bg-gray-100 rounded-full animate-pulse"
                    />
                ))}
            </div>
            {/* Filter chips row */}
            <div className="border-b border-gray-100 px-5 pb-3">
                <div className="h-3 w-24 bg-gray-100 rounded mb-2 animate-pulse" />
                <div className="flex flex-wrap gap-2">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="h-6 w-16 bg-gray-100 rounded-full animate-pulse"
                        />
                    ))}
                </div>
            </div>
            {/* Coaching moment cards */}
            <div className="px-5 pb-4 space-y-4">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="rounded-lg border border-gray-100 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="h-2.5 w-2.5 rounded-full bg-gray-200 animate-pulse" />
                            <div className="h-3.5 w-2/5 bg-gray-200 rounded animate-pulse" />
                        </div>
                        <div className="space-y-2">
                            <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                            <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 space-y-1.5">
                            <div className="h-2.5 w-20 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
